import { Autocomplete, Chip, TextField } from "@mui/material";
import { ReactNode, SyntheticEvent } from "react";

export type SelectOption = { value: string; label: string };

type MultiSelectFilterProps = {
  field: string;
  label: string;
  options: SelectOption[];
  value: string[];
  onChange: (field: string, values: string[]) => void;
  renderChip?: (
    value: string,
    onDelete: (event: SyntheticEvent) => void,
  ) => ReactNode;
  freeSolo?: boolean;
};

type OptionValue = SelectOption | string;

const getOptionValue = (o: OptionValue): string =>
  typeof o === "string" ? o : o.value;

const getOptionLabel = (o: OptionValue): string =>
  typeof o === "string" ? o : o.label;

export function MultiSelectFilter({
  field,
  label,
  options,
  value,
  onChange,
  renderChip,
  freeSolo,
}: MultiSelectFilterProps) {
  const selectedOptions: OptionValue[] = value.map(
    (v) => options.find((o) => o.value === v) ?? v,
  );

  return (
    <Autocomplete
      multiple
      freeSolo={freeSolo}
      size="small"
      options={options as OptionValue[]}
      getOptionLabel={getOptionLabel}
      value={selectedOptions}
      onChange={(_, newValue) =>
        onChange(field, newValue.map(getOptionValue))
      }
      isOptionEqualToValue={(o, v) => getOptionValue(o) === getOptionValue(v)}
      renderTags={(tagValues, getTagProps) =>
        tagValues.map((option, index) => {
          const { key, onDelete, ...tagProps } = getTagProps({ index });
          if (renderChip) {
            return (
              <span key={key}>
                {renderChip(getOptionValue(option), onDelete)}
              </span>
            );
          }
          return (
            <Chip
              key={key}
              {...tagProps}
              onDelete={onDelete}
              label={getOptionLabel(option)}
              size="small"
            />
          );
        })
      }
      renderInput={(params) => <TextField {...params} label={label} />}
      sx={{ minWidth: 180 }}
    />
  );
}
